import {
  ComponentRef,
  Injectable,
  InjectionToken,
  Injector,
  OnInit,
} from '@angular/core';
import { ResizeService } from '../resize/resize.service';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MatBottomSheet,
  MatBottomSheetConfig,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { ComponentType } from '@angular/cdk/portal';
import { ShareLinkPopUpComponent } from '../../components/pop-ups/share-link/share-link-pop-up.component';
import { BottomSheetComponent } from '../../components/design/bottom-sheet/bottom-sheet/bottom-sheet.component';
import { DialogComponent } from '../../components/design/dialog/dialog/dialog.component';
import { CardDesignComponent } from '../../components/design/slide-up-card/card-design.component';
import { SpecialMessagesPopUpComponent } from '../../components/pop-ups/special-messages/special-messages-pop-up.component';
import { SpecialMessage } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/special-messages/special-message.interface';
import { StationCanceledPopUpComponent } from '../../components/pop-ups/station-canceled/station-canceled-pop-up.component';
import { StopFullData } from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { AgencyContactInfoPopUpComponent } from '../../components/pop-ups/agency-contact-info/agency-contact-info-pop-up.component';
import { LocationUsePopUpComponent } from '../../components/pop-ups/location-use/location-use-pop-up.component';
import { CookiesPopUpComponent } from '../../components/pop-ups/cookies/cookies-pop-up.component';
import { switchMap } from 'rxjs';
import { LanguageService } from '../language/language.service';
import { LINK_COPIED } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { ToastService } from '../toast/toast.service';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { ErrorMessagePopUpComponent } from '../../components/pop-ups/error-message-pop-up/error-message-pop-up.component';
import { HttpErrorResponse } from '@angular/common/http';
import { CantFindUserLocationPopUpComponent } from '../../components/pop-ups/cant-find-user-location/cant-find-user-location-pop-up.component';
import { RequestSentPopUpComponent } from '../../components/pop-ups/request-sent/request-sent-pop-up.component';
import { StationNotActivePopUpComponent } from '../../components/pop-ups/station-not-active/station-not-active-pop-up.component';
import { RouteNotActivePopUpComponent } from '../../components/pop-ups/route-not-active/route-not-active-pop-up.component';
import { SiteIntroPopUpComponent } from '../../components/pop-ups/site-intro/site-intro-pop-up.component';
import { StopsChangedPopUpComponent } from '../../components/pop-ups/stops-changed/stops-changed-pop-up.component';

export const POPUP_DATA = new InjectionToken<any>('POPUP_DATA');

@Injectable({
  providedIn: 'root',
})
export class ModalService extends ResizeService {
  public popUpRef: MatDialogRef<any, any> | MatBottomSheetRef = null;
  private copyMessage: string = 'Link copied to clipboard!'; // Default message

  constructor(
    public readonly dialog: MatDialog,
    private readonly bottomSheet: MatBottomSheet,
    private readonly injector: Injector,
    private readonly languageService: LanguageService,
    private readonly toastService: ToastService
  ) {
    super();

    this.onDisplayModeChange$.subscribe((mode) => {
      this.closePopUp();
    });

    // Subscribe to language changes to update the copy message
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.copyMessage = LINK_COPIED[language.language];
    });
  }

  public openDialog<T>(
    component: ComponentType<T>,
    options?: MatDialogConfig<any> & {
      data?: { [key: string]: any };
    }
  ) {
    const injector = Injector.create({
      providers: [
        { provide: POPUP_DATA, useValue: { component, data: options?.data } },
      ],
      parent: this.injector,
    });

    // a11y: we manage initial focus ourselves via our DialogComponent's CDK
    // FocusTrap (`focusInitialElementWhenReady`, which honours `cdkFocusInitial`).
    // Material's own autoFocus runs synchronously before the popup body has
    // rendered and may land on whatever it can find first (e.g. the Cancel
    // button at the bottom of the form). Disabling it here lets our trap put
    // focus on the close-X button at the top, which is what the a11y audit
    // requires across all popups.
    options = { autoFocus: false, ...options, injector: injector };

    this.popUpRef = this.dialog.open(component, options);
    return this.popUpRef.afterClosed();
  }

  public openPopUp<T = CardDesignComponent>(
    component: ComponentType<T>,
    options?: MatDialogConfig<any> &
      MatBottomSheetConfig<any> & {
        data?: { [key: string]: any };
      }
  ) {
    const injector = Injector.create({
      providers: [
        { provide: POPUP_DATA, useValue: { component, data: options?.data } },
      ],
      parent: this.injector,
    });

    // a11y: same rationale as openDialog — keep Material's auto-focus off so
    // the close button (marked with `cdkFocusInitial`) wins as the initial
    // focus target, as required by the audit.
    options = { autoFocus: false, ...options, injector: injector };

    if (this.isMobile) {
      this.popUpRef = this.bottomSheet.open(BottomSheetComponent, options);
      return this.popUpRef.afterDismissed();
    } else {
      this.popUpRef = this.dialog.open(DialogComponent, options);
      return this.popUpRef.afterClosed();
    }
  }

  public openShareLinkModal(link?: string) {
    const shareUrl = link || window.location.href;
    // Check if the link is an error object (typically JSON string starting with '{')
    if (typeof shareUrl === 'string' && shareUrl.trim().startsWith('"{')) {
      return;
    }

    // Use native share on mobile if available
    if (this.isMobile && navigator.share) {
      navigator
        .share({
          url: shareUrl,
        })
        .catch((error) => {
          console.error('Error sharing:', error);
          // Fallback to modal if sharing fails
          this.openPopUp(ShareLinkPopUpComponent, {
            data: {
              link: shareUrl,
            },
            hasBackdrop: true,
          });
        });
    } else {
      // On desktop, copy to clipboard and show toast notification
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          this.toastService.showToast(this.copyMessage, {
            position: 'top-right',
            duration: 2000,
          });
        })
        .catch((error) => {
          console.error('Failed to copy link:', error);
          // Fallback to modal if clipboard copy fails
          this.openPopUp(ShareLinkPopUpComponent, {
            data: {
              link: shareUrl,
            },
            hasBackdrop: true,
          });
        });
    }
  }

  public openSpecialMessagesModal(messages: SpecialMessage[]) {
    this.openPopUp(SpecialMessagesPopUpComponent, {
      data: {
        messages,
      },
      hasBackdrop: true,
    });
  }

  public openStationCanceledModal(station: StopFullData) {
    this.openPopUp(StationCanceledPopUpComponent, {
      data: {
        station,
      },
      hasBackdrop: true,
    });
  }

  public openStationNotActiveModal() {
    this.openPopUp(StationNotActivePopUpComponent, {
      hasBackdrop: true,
    });
  }

  public openRouteNotActiveModal(routeId: number) {
    this.openPopUp(RouteNotActivePopUpComponent, {
      data: { routeId },
      hasBackdrop: true,
    });
  }

  public openStopsChangedModal(removedBoardingStop: Stop | null, removedDropingStop: Stop | null) {
    this.openPopUp(StopsChangedPopUpComponent, {
      data: { removedBoardingStop, removedDropingStop },
      hasBackdrop: true,
    });
  }

  public openAgencyContactInfoModal(agencyId: number, isForOrderSeat) {
    this.openPopUp(AgencyContactInfoPopUpComponent, {
      data: { agencyId, isForOrderSeat },
      hasBackdrop: true,
    });
  }

  public openLocationUsePermissionModal() {
    return this.openPopUp(LocationUsePopUpComponent, {
      hasBackdrop: true,
    });
  }

  public openLocationPermissionDeniedModal() {
    return this.openPopUp(LocationUsePopUpComponent, {
      data: { permissionDenied: true },
      hasBackdrop: true,
    });
  }

  public openCantFindUserLocationModal(message: {hebrew: string, english: string, arabic: string}) {
    return this.openPopUp(CantFindUserLocationPopUpComponent, {
      data: {message},
      hasBackdrop: true
    })
  }

  public openAllowCookiesModal() {
    return this.openPopUp(CookiesPopUpComponent, {
      data: { hideDivider: true },
      hasBackdrop: false,
      minWidth: '100%',
      position: {
        bottom: '0px',
      },
      panelClass: ['bottom-flat', 'cookies-pop-up', 'shadow-2xl'],
    });
  }

  public openErrorModal(error: any) {
   

    return this.openPopUp(ErrorMessagePopUpComponent, {
      data: error,
      hasBackdrop: true,
    });
  }

  public openSiteIntroModal(videoUrl: string, moreInfoUrl: string) {
    return this.openPopUp(SiteIntroPopUpComponent, {
      data: { videoUrl, moreInfoUrl },
      hasBackdrop: true,
      width: '35rem',
      maxWidth: '95vw',
    });
  }

  public openRequestSentPopUp() {
    return this.openPopUp(RequestSentPopUpComponent, {
      hasBackdrop: true,
    });
  }

  public closePopUp(): void {
    if (!!this.popUpRef) {
      if (this.popUpRef instanceof MatDialogRef) this.popUpRef.close();
      else if (this.popUpRef instanceof MatBottomSheetRef)
        this.popUpRef.dismiss();

      this.popUpRef = null;
    }
  }
}
